import i18n from "@/i18n";
import axios from "axios";
import apiService from "@/services/apiService";
import { v4 as uuidv4 } from "uuid";
import {
  DeviceConfiguration,
  DeviceConfigurationDeserializer,
  DeviceUpdates,
  DeviceUpdatesDeserializer,
} from "@/models/deviceConfiguration";
import { WizardConfiguration } from "@/models/wizardConfiguration";
import LicenseDetailModel, {
  LicenseDetail,
  LicenseProduct,
  LicenseProductDeserializer,
} from "@/models/license";
import router from "@/router";

const connectToRoom = async (
  pin: string,
  type: string
): Promise<DeviceConfiguration | undefined> => {
  const res = await axios.get(apiService.wizard.connect() + "/" + pin, {
    // params: { configurationType: update ? "ChangeLicense" : "Complete" },
    params: { configurationType: type },
  });
  if (res.data.isPinValid == false) {
    throw new Error(i18n.t("roomWizard.errors.invalidPin") as string);
  }
  if (res?.data?.configurationType !== type) {
    switch (res.data.configurationType) { 
      case "Complete":
        router.push(`/activate/${pin}?wrongConfigurationType=true`);
        return;
      case "LicenseRegistration":
        router.push(`/register/${pin}?wrongConfigurationType=true`);
        return;

      case "ChangeLicense":
        router.push(`/change-license/${pin}?wrongConfigurationType=true`);
        return;
      default:
        return;
    }
  }
  if (res.data.deviceConnected == false) {
    throw new Error(i18n.t("roomWizard.errors.deviceNotConnected") as string);
  }
  if (
    res.data.deviceConfiguration?.status == "WebWizardCompleted" ||
    res.data.deviceConfiguration?.status == "ClientWizardRunning" ||
    res.data.deviceConfiguration?.status == "ClientWizardCompleted"
  ) {
    throw new Error(i18n.t("roomWizard.errors.wizardStatus") as string);
  }
  return DeviceConfigurationDeserializer(
    res.data.deviceConfiguration,
    res.data.deviceType
  );
};

const reserveSeat = async (
  deviceConfigurationId: string,
  licensePackageId: string
): Promise<any> => {
  try {
    const res = await axios.post(apiService.wizard.reserveSeat(), {
      DeviceConfigurationId: deviceConfigurationId,
      LicensePackageId: licensePackageId,
    });
    return res.data;
  } catch (err) {
    if (err?.response?.data?.errorCode === "L404") {
      const errorMsg = JSON.parse(err?.response?.data?.message);
      throw new Error(errorMsg.message);
    } else {
      throw err;
    }
  }
};

const getTrials = async (
  tenantCode: string
): Promise<Array<LicenseProduct>> => {
  const res = await axios.get(apiService.wizard.getTrials(tenantCode));
  return res.data.map(LicenseProductDeserializer);
};

const startTrial = async (
  deviceConfigurationId: string,
  tenantCode: string,
  sku: string
): Promise<LicenseDetail> => {
  try {
    const res = await axios.post(apiService.wizard.startTrial(), {
      DeviceConfigurationId: deviceConfigurationId,
      tenantCode: tenantCode,
      sku: sku,
    });
    return LicenseDetailModel.deserialize(res.data);
  } catch (err) {
    if (err?.response?.data?.errorCode === "L422") {
      throw new Error(err?.response?.data?.message);
    } else {
      throw err;
    }
  }
};

const checkUpdates = async (
  deviceConfiguration: DeviceConfiguration
): Promise<DeviceUpdates | undefined> => {
  const res = await axios.post(apiService.wizard.checkUpdates(), {
    label: deviceConfiguration.label,
    podReleaseTypeKeyId: deviceConfiguration.releaseType,
    currentVersion: deviceConfiguration.installedVersion,
    releaseChannel: deviceConfiguration.releaseChannel,
    //"useFastChannel: false //[true/false] // (opzionale, non necessario in questo momento dato che non gestito da wizard)
  });
  return res.data ? DeviceUpdatesDeserializer(res.data) : undefined;
};

const updateDevice = async (
  deviceConfiguration: DeviceConfiguration,
  downloadUrl: string
): Promise<any> => {
  const res = await axios.post(apiService.wizard.configurationEvents(), {
    EventId: uuidv4(),
    DeviceConfigurationId: deviceConfiguration.deviceConfigurationId,
    EventKey: "update-required",
    EventData: {
      downloadUrl: downloadUrl,
    },
  });
  return res.data;
};

const registrationComplete = async (
  deviceConfiguration: DeviceConfiguration
): Promise<any> => {
  const res = await axios.post(apiService.wizard.configurationEvents(), {
    EventId: uuidv4(),
    DeviceConfigurationId: deviceConfiguration.deviceConfigurationId,
    EventKey: "license-registration-completed",
  });
  return res.data;
};

const sendConfig = async (
  deviceConfiguration: DeviceConfiguration,
  wizardConfiguration: WizardConfiguration
): Promise<any> => {
  const res = await axios.post(apiService.wizard.sendConfiguration(), {
    DeviceConfigurationId: deviceConfiguration.deviceConfigurationId,
    JsonConfiguration: wizardConfiguration,
  });
  return res.data;
};

export default {
  connectToRoom,
  reserveSeat,
  checkUpdates,
  updateDevice,
  sendConfig,
  startTrial,
  getTrials,
  registrationComplete,
};
